/**
 * External Heat Pump Unit Component
 * Displays the outdoor unit with pipes, temperature readings, and COP meter
 */

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import externalUnitHeatPump from "./externalUnitHeatPump.png";
import externalUnitHeatPumpFan from "./externalUnitHeatPumpFan.gif";
import outdoorWeatherIcon from "./outdoorWeather.png";
import HeatCraftHPLogo from "./HeatCraftHPLogo.png";
import RWTHLogo from "../../Shared/RWTH_Logo.png";

/**
 * External Unit Display
 * Shows outdoor temperature, heat pump unit, pipes, and system pressure
 */
export const ExternalUnit = ({
  outdoorTemperature,
  supplyTemperature,
  returnTemperature,
  systemPressure,
  cop,
  isRunning,
  styles,
}) => {
  const { t } = useTranslation();
  
  return (
    <svg data-testid="external-unit-display">
      {/* Base */}
      <rect x={15} width={657} y={480} height={10} fill="rgba(0,0,0,0.3)" stroke="transparent" />

      {/* Logo */}
      <image href={HeatCraftHPLogo} x={15} y={505} width={200} />

      {/* Logo */}
      <image href={RWTHLogo} x={547} y={505} width={250} />

      {/* External unit */}
      <image href={externalUnitHeatPump} width={65} x={20} y={428} />

      {/* Animated fan when running */}
      {isRunning && (
        <image href={externalUnitHeatPumpFan} width={31} x={25} y={433} />
      )}

      {/* Cold pipe (return) */}
      <rect x={35} y={380} height={48} width={8} fill="rgba(70, 130, 180,0.5)" stroke="transparent" />
      <rect x={37} y={360} height={5} width={3} fill="rgba(70, 130, 180,0.5)" stroke="transparent" />
      <rect x={37} y={370} height={5} width={3} fill="rgba(70, 130, 180,0.5)" stroke="transparent" />

      {/* Hot pipe (supply) */}
      <rect x={60} y={380} height={48} width={8} fill="rgba(255,0,0,0.5)" stroke="transparent" />
      <rect x={62} y={360} height={5} width={3} fill="rgba(255,0,0,0.5)" stroke="transparent" />
      <rect x={62} y={370} height={5} width={3} fill="rgba(255,0,0,0.5)" stroke="transparent" />

       {/* Professional Dashboard Panel */}
      <g data-testid="external-unit-dashboard">
        {/* Main Dashboard Background */}
        <rect x={17} y={250} width={75} height={97} rx={4} fill="#ffffff" stroke="#bdc3c7" strokeWidth={2} />
        <rect x={18} y={251} width={73} height={18} rx={4} fill="#ecf0f1" />
        
        {/* Device Indicator LEDs */}
        <g>
          <rect x={42} y={257} width={6} height={2} rx={1} fill="#27ae60" opacity={0.8} />
          <rect x={51.5} y={257} width={6} height={2} rx={1} fill="#3498db" opacity={0.8} />
          <rect x={61} y={257} width={6} height={2} rx={1} fill="#95a5a6" opacity={0.6} />
        </g>

        {/* COP Section */}
        <g className="cop-display" data-testid="cop-display">
          <text x={24} y={282} fill="#7f8c8d" fontSize={8} fontWeight="600">COP</text>
          <text x={54.5} y={297} fill="#27ae60" fontSize={18} fontWeight="bold" textAnchor="middle">
            {cop.toFixed(2)}
          </text>
        </g>

        {/* Divider Line */}
        <line x1={22} y1={305} x2={87} y2={305} stroke="#bdc3c7" strokeWidth={1} />

        {/* Temperature & Pressure Readings */}
        <g data-testid="external-unit-readings">
          {/* Supply Temperature */}
          <g data-testid="supply-temperature-display">
            <circle cx={32} cy={315} r={3} fill="#e74c3c" />
            <text x={39} y={318} fill="#2c3e50" fontSize={9} fontWeight="600">
              {supplyTemperature}°C
            </text>
          </g>
          
          {/* Return Temperature */}
          <g className="return-temp-display" data-testid="return-temp-display">
            <circle cx={32} cy={327} r={3} fill="#3498db" />
            <text x={39} y={330} fill="#2c3e50" fontSize={9} fontWeight="600">
              {returnTemperature.toFixed(1)}°C
            </text>
          </g>
          
          {/* System Pressure */}
          <g data-testid="system-pressure-display">
            <text x={50} y={342} fill="#2c3e50" fontSize={9} fontWeight="bold" textAnchor="middle">
              {isRunning ? systemPressure : 0} bar
            </text>
          </g>
        </g>
      </g>

      {/* Outdoor temperature */}
      <g className="outdoor-temperature-display" data-testid="outdoor-temp-room">
        <Tooltip 
          title={t('heatPumpSimulation.external.outdoorTemperature')}
          color="white"
        >
          <rect 
            x={10} 
            y={10} 
            width={80} 
            height={30} 
            fill="transparent" 
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
        <text x={40} y={28} fill="red" fontSize={16} style={{ pointerEvents: 'none' }}>
          {outdoorTemperature} °C
        </text>

        <image href={outdoorWeatherIcon} width={25} x={10} y={10} />
      </g>
    </svg>
  );
};

ExternalUnit.propTypes = {
  outdoorTemperature: PropTypes.number.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  returnTemperature: PropTypes.number.isRequired,
  systemPressure: PropTypes.number.isRequired,
  cop: PropTypes.number.isRequired,
  isRunning: PropTypes.bool.isRequired,
  styles: PropTypes.object,
};

ExternalUnit.defaultProps = {
  styles: {},
};
